<template>
  <div class="w-full">
    <div
      v-if="editable"
      class="w-full relative"
    >
      <PendingBuildingActions
        v-if="placing"
        :duplication="canBeDuplicated"
        :removal="canDeletePendingBuilding"
        :rotation="canRotate"
        @duplicate="duplicate"
        @remove="remove"
        @rotate="rotate"
        @cancel="cancel"
      />
      <EditorActions
        v-else
        :leveling="canLevelUp"
        @levelUp="levelUp"
        @lockExpansions="lockExpansions"
        @save="saveLayout"
      />
    </div>
    <PreviewActions
      v-else
      @copy="copyLayout"
    />
  </div>
</template>

<script>
import BuildingType from '@/roc/building/BuildingType';
import Eras from '@/roc/era';
import PendingBuildingActions from '@/components/Grid/Toolbar/PendingBuildingActions.vue';
import PreviewActions from '@/components/Grid/Toolbar/PreviewActions.vue';
import EditorActions from '@/components/Grid/Toolbar/EditorActions.vue';
import {computed, onBeforeMount, onBeforeUnmount} from 'vue';
import {useApplicationStore} from '@/roc/stores/ApplicationStore';

export default {
  components: {
    EditorActions,
    PreviewActions,
    PendingBuildingActions
  },
  props: {
    placing: {
      type: Boolean,
      required: true
    },
    pendingBuilding: {
      type: Object,
      required: false,
      default: () => {
        return null;
      }
    }
  },
  emits: ['duplicate', 'remove', 'rotate', 'cancel', 'save', 'copy', 'levelUp', 'lockExpansions'],
  setup(props, context) {
    const appStore = useApplicationStore();

    const canDeletePendingBuilding = computed(() => {
      return props.pendingBuilding !== null && BuildingType.CITY_HALL !== props.pendingBuilding.type;
    });

    const canBeDuplicated = computed(() => {
      if (props.pendingBuilding !== null) {
        const element = document.querySelector('.building-menu-item[data-building-id="' + props.pendingBuilding.id + '"]');
        if (element) {
          return element && !element.classList.contains('count-maxed');
        }
      }
      return false;
    });

    const canLevelUp = computed(() => {
      return Object.keys(Eras).indexOf(appStore.selectedEra) + 2 <= Object.keys(Eras).length;
    });

    const canRotate = computed(() => {
      return props.pendingBuilding !== null && props.pendingBuilding.width !== props.pendingBuilding.height;
    });

    const onKeyPress = (ev) => {
      switch (ev.keyCode) {
        case 46:
        case 88:
          remove();
          break;
        case 27:
          cancel();
          break;
        case 82:
          rotate();
          break;
        case 68:
          duplicate();
          break;
      }
    };

    const cancel = () => {
      context.emit('cancel');
    };

    const rotate = () => {
      if (canRotate.value) {
        context.emit('rotate');
      }
    };

    const duplicate = () => {
      if (canBeDuplicated.value) {
        context.emit('duplicate');
      }
    };

    const remove = () => {
      if (canDeletePendingBuilding.value) {
        context.emit('remove');
      }
    };

    const saveLayout = () => {
      context.emit('save');
    };

    const copyLayout = () => {
      context.emit('copy');
    };

    const levelUp = () => {
      context.emit('levelUp');
    };

    const lockExpansions = () => {
      context.emit('lockExpansions');
    };

    onBeforeMount(() => {
      document.addEventListener('keyup', onKeyPress);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keyup', onKeyPress);
    });

    return {
      canDeletePendingBuilding,
      canBeDuplicated,
      canLevelUp,
      canRotate,

      onKeyPress,

      cancel,
      rotate,
      duplicate,
      remove,
      saveLayout,
      copyLayout,
      levelUp,
      lockExpansions,

      editable: computed(() => appStore.editable)
    };
  }
};
</script>
